import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import {
    Pencil,
    Trash2,
    MoreVertical,
    Search,
    ChevronUp,
    ChevronDown,
} from "lucide-react";
import Button from "./Button";

const MIN_COL_WIDTH = 80;
const DEFAULT_WIDTH = 150;

export const DataTable = ({
    columns,
    data,
    onEdit,
    onDelete,
    onAction,
    rowActions = [],
    title,
    classNames,
}) => {
    const [search, setSearch] = useState("");
    const [openDropdownRow, setOpenDropdownRow] = useState(null);
    const [dropdownDirection, setDropdownDirection] = useState("down");
    const [resizing, setResizing] = useState(false);
    const [widths, setWidths] = useState({});

    const resizeData = useRef({ startWidth: 0, startX: 0, columnKey: null });
    const buttonRefs = useRef({});

    // --- Column Resizing Persistence ---
    useEffect(() => {
        const storedWidths = localStorage.getItem('datatable_col_widths');
        if (storedWidths) {
            setWidths(JSON.parse(storedWidths));
        } else {
            const initialWidths = columns.reduce((acc, col) => {
                acc[col.key] = DEFAULT_WIDTH;
                return acc;
            }, {});
            setWidths(initialWidths);
        }
    }, [columns]);

    const handleMouseDown = useCallback((e, colKey) => {
        e.stopPropagation();
        setResizing(true);
        resizeData.current.startX = e.clientX;
        resizeData.current.startWidth = widths[colKey] || DEFAULT_WIDTH;
        resizeData.current.columnKey = colKey;
        document.body.classList.add('resizing-datatable');
    }, [widths]);

    const handleMouseMove = useCallback((e) => {
        if (!resizing) return;
        const { startX, startWidth, columnKey } = resizeData.current;
        const delta = e.clientX - startX;
        let newWidth = Math.max(startWidth + delta, MIN_COL_WIDTH);
        setWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    }, [resizing]);

    const handleMouseUp = useCallback(() => {
        if (!resizing) return;
        setResizing(false);
        document.body.classList.remove('resizing-datatable');
        if (resizeData.current.columnKey) {
            localStorage.setItem('datatable_col_widths', JSON.stringify(widths));
        }
    }, [resizing, widths]);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    // --- Search Logic ---
    const filteredData = useMemo(() => {
        if (!search.trim()) return data;
        const lower = search.toLowerCase();
        return data.filter((row) =>
            columns.some((col) => {
                const val = row[col.key];
                return val != null && String(val).toLowerCase().includes(lower);
            })
        );
    }, [search, data, columns]);

    // --- Portal Logic (Dropdown) ---
    const handleDropdownToggle = useCallback((index, e) => {
        e.stopPropagation();
        if (openDropdownRow === index) {
            setOpenDropdownRow(null);
            return;
        }
        const btn = e.currentTarget;
        buttonRefs.current[index] = btn;
        const rect = btn.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setDropdownDirection(spaceBelow < 180 ? "up" : "down");
        setOpenDropdownRow(index);
    }, [openDropdownRow]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openDropdownRow === null) return;
            const btn = buttonRefs.current[openDropdownRow];
            const menu = document.getElementById("datatable-portal-menu");
            if ((btn && btn.contains(e.target)) || (menu && menu.contains(e.target))) return;
            setOpenDropdownRow(null);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openDropdownRow]);

    const PortalMenu = () => {
        if (openDropdownRow === null) return null;
        const btn = buttonRefs.current[openDropdownRow];
        if (!btn) return null;
        const rect = btn.getBoundingClientRect();
        const top = dropdownDirection === "up" ? rect.top + window.scrollY : rect.bottom + window.scrollY;
        const left = Math.min(rect.right + window.scrollX - 192, window.innerWidth - 200);

        return ReactDOM.createPortal(
            <div
                id="datatable-portal-menu"
                className="fixed z-[9999] w-48 bg-white border border-gray-200 rounded-lg shadow-2xl"
                style={{
                    top: dropdownDirection === "up" ? "auto" : top,
                    bottom: dropdownDirection === "up" ? window.innerHeight - rect.top : "auto",
                    left: Math.max(left, 10),
                }}
            >
                <div className="py-1">
                    {rowActions.map((action) => (
                        <button
                            key={action.key}
                            onClick={(e) => {
                                e.stopPropagation();
                                onAction?.(action.key, filteredData[openDropdownRow]);
                                setOpenDropdownRow(null);
                            }}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b last:border-0 border-gray-50"
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            </div>,
            document.body
        );
    };

    return (
        <div className={`flex flex-col bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${classNames || ""}`}>
            {/* Header: Title & Search */}
            <div className="p-4 sm:p-6 border-b border-gray-100 bg-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                {title && <h3 className="text-xl font-bold text-gray-800">{title}</h3>}
                <div className="relative w-full sm:max-w-xs">
                    <input
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gmail-blue outline-none transition-all"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>
            </div>

            {/* Table Container */}
            <div className="flex-1 overflow-auto min-w-0">
                {/* Desktop View */}
                <div className="hidden lg:block">
                    <table className="w-full table-fixed text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-20">
                            <tr>
                                {columns.map((col) => (
                                    <th
                                        key={col.key}
                                        className="px-6 py-4 text-left font-bold text-gray-500 uppercase tracking-wider text-[11px] relative group"
                                        style={{ width: widths[col.key] || DEFAULT_WIDTH }}
                                    >
                                        <div className="flex items-center">
                                            {col.label}
                                            <div
                                                onMouseDown={(e) => handleMouseDown(e, col.key)}
                                                className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize z-10 hover:bg-gmail-blue/30 transition-colors"
                                            />
                                        </div>
                                    </th>
                                ))}
                                {(onEdit || onDelete || rowActions.length > 0) && (
                                    <th className="px-6 py-4 text-right font-bold text-gray-500 text-[11px] uppercase tracking-wider w-[140px]">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length + 1} className="py-20 text-center text-gray-400">
                                        No matching records found.
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((row, i) => (
                                    <motion.tr key={row.id || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-blue-50/20 transition-colors group">
                                        {columns.map((col) => (
                                            <td key={col.key} className="px-6 py-4 truncate group-hover:whitespace-normal" style={{ width: widths[col.key] || DEFAULT_WIDTH }}>
                                                {col.render ? col.render(row[col.key], row) : row[col.key] || "-"}
                                            </td>
                                        ))}
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-1">
                                                {rowActions.length > 0 && (
                                                    <button onClick={(e) => handleDropdownToggle(i, e)} className="p-2 hover:bg-gray-100 rounded-full text-gray-500"><MoreVertical size={18} /></button>
                                                )}
                                                {onEdit && <button onClick={() => onEdit(row)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-full"><Pencil size={18} /></button>}
                                                {onDelete && <button onClick={() => onDelete(row)} className="p-2 hover:bg-red-50 text-red-500 rounded-full"><Trash2 size={18} /></button>}
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View: Card Grid Strategy */}
                {/* <div className="lg:hidden p-4 space-y-4 bg-gray-50/50">
                    {filteredData.map((row, i) => (
                        <motion.div key={row.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                    {columns.map((col, cIdx) => (
                                        <div key={col.key} className={cIdx === 0 ? "col-span-2 border-b border-gray-50 pb-2" : ""}>
                                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-tight">{col.label}</span>
                                            <span className={`block ${cIdx === 0 ? "text-base font-bold text-gray-900" : "text-sm text-gray-700"} truncate`}>
                                                {col.render ? col.render(row[col.key], row) : row[col.key] || "-"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                {(onEdit || onDelete || rowActions.length > 0) && (
                                    <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                                        {onEdit && <button onClick={() => onEdit(row)} className="p-2.5 bg-blue-50 text-blue-600 rounded-lg"><Pencil size={18} /></button>}
                                        {onDelete && <button onClick={() => onDelete(row)} className="p-2.5 bg-red-50 text-red-500 rounded-lg"><Trash2 size={18} /></button>}
                                        {rowActions.length > 0 && (
                                            <button onClick={(e) => handleDropdownToggle(i, e)} className="p-2.5 bg-gray-100 text-gray-600 rounded-lg"><MoreVertical size={18} /></button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div> */}
                {/* MOBILE VIEW: Forced 100% Width (No Horizontal Scroll) */}
                <div className="lg:hidden p-3 space-y-3 bg-gray-50/50 max-w-full overflow-x-hidden">
                    {filteredData.map((row, i) => (
                        <motion.div
                            key={row.id || i}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white border border-gray-200 rounded-lg shadow-sm w-full"
                        >
                            <div className="p-3 space-y-3">
                                {/* 1. Header: Primary Column */}
                                <div className="border-b border-gray-50 pb-2">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                        {columns[0].label}
                                    </p>
                                    {/* min-w-0 and truncate are essential to prevent "width-pushing" */}
                                    <p className="text-sm font-bold text-gray-900 truncate min-w-0">
                                        {columns[0].render ? columns[0].render(row[columns[0].key], row) : row[columns[0].key]}
                                    </p>
                                </div>

                                {/* 2. Content Grid: Using minmax(0, 1fr) to force containment */}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                    {columns.slice(1).map((col) => (
                                        <div key={col.key} className="min-w-0 flex flex-col">
                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter truncate">
                                                {col.label}
                                            </span>
                                            <span className="text-xs text-gray-700 truncate min-w-0">
                                                {col.render ? col.render(row[col.key], row) : row[col.key] || "-"}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* 3. Action Strip */}
                                {(onEdit || onDelete || rowActions.length > 0) && (
                                    <div className="flex justify-end items-center gap-1 pt-2 border-t border-gray-100">
                                        {onEdit && (
                                            <button onClick={() => onEdit(row)} className="p-2 text-gray-500 rounded-md hover:bg-gray-50">
                                                <Pencil size={16} />
                                            </button>
                                        )}
                                        {onDelete && (
                                            <button onClick={() => onDelete(row)} className="p-2 text-gray-400 hover:text-gmail-red rounded-md hover:bg-red-50">
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                        {rowActions.length > 0 && (
                                            <button
                                                onClick={(e) => handleDropdownToggle(i, e)}
                                                className="p-2 text-gray-500 rounded-md hover:bg-gray-100"
                                            >
                                                <MoreVertical size={16} />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            <PortalMenu />
            <style jsx global>{`
                .resizing-datatable { user-select: none; cursor: col-resize; }
                .no-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>
        </div>
    );
};